/* External dependencies */
import { createSelector } from "reselect";
import _flatMap from "lodash/flatMap";


/* Internal dependencies */
import { createAllOfEntitySelector, createEntityByIdSelector } from "./factories";

/**
 * Returns all subscriptions in the state.
 *
 * Deliberately not exported. Reading every subscription is what the presentation read models replaced —
 * for a large account that is a multi-megabyte fetch to answer a question the server can answer directly.
 * It survives only because `getProductSwitches` is built on it and still has a consumer.
 *
 * @function
 *
 * @param {Object} state Application state.
 *
 * @returns {Array} All subscriptions.
 */
const getSubscriptions = createAllOfEntitySelector( "subscriptions" );

/**
 * Returns all subscriptions in the state ordered by ID.
 *
 * @function
 *
 * @param {Object} state Application state.
 *
 * @returns {Array} All subscriptions ordered by ID.
 */
export const getAllSubscriptionsById = createEntityByIdSelector( "subscriptions" );


/**
 * Returns every product switch across all subscriptions.
 *
 * The last selector here still built on the whole collection. Its one consumer is the invoice download
 * list, which needs switches for subscriptions the customer may no longer hold — retiring it means moving
 * that screen onto a read model, which is a separate piece of work.
 *
 * @function
 *
 * @param {Object} state Application state.
 *
 * @returns {Array} All product switches.
 */
export const getProductSwitches = createSelector(
	getSubscriptions,
	( subscriptions ) => {
		if ( ! subscriptions ) {
			return {};
		}
		return _flatMap( subscriptions, subscription => subscription.productSwitches );
	},
);
